import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import type { ReactNode, Ref, RefObject } from 'react';
import {
  createContext,
  use,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  EnrichedTextInput,
  type EnrichedTextInputInstance,
  type OnChangeStateEvent,
} from 'react-native-enriched-html';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import Box from '@/components/UI/Box';
import Button from '@/components/UI/Button';
import Text from '@/components/UI/Text';
import { richTextSystemFontFamily } from './RichText.utils';

export type RichTextComposerInstance = {
  focus: () => void;
};

type RichTextComposerProps = {
  accessibilityLabel?: string;
  autoFocus?: boolean;
  defaultValue?: string;
  errorMessage?: string;
  onChangeHtml?: (html: string) => void;
  onChangeText?: (text: string) => void;
  placeholder?: string;
  ref?: Ref<RichTextComposerInstance>;
  children: ReactNode;
};

type FormatState = OnChangeStateEvent['bold'];
const underlineTextDecoration: 'underline' = 'underline';

type ComposerAction = {
  getState: (state: OnChangeStateEvent) => FormatState;
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  label: string;
  onPress: (input: EnrichedTextInputInstance) => void;
  testId: string;
};

const composerActions: ComposerAction[] = [
  {
    getState: (state) => state.bold,
    icon: 'format-bold',
    label: 'Bold',
    onPress: (input) => input.toggleBold(),
    testId: 'bold',
  },
  {
    getState: (state) => state.italic,
    icon: 'format-italic',
    label: 'Italic',
    onPress: (input) => input.toggleItalic(),
    testId: 'italic',
  },
  {
    getState: (state) => state.underline,
    icon: 'format-underline',
    label: 'Underline',
    onPress: (input) => input.toggleUnderline(),
    testId: 'underline',
  },
  {
    getState: (state) => state.orderedList,
    icon: 'format-list-numbered',
    label: 'Numbered list',
    onPress: (input) => input.toggleOrderedList(),
    testId: 'numbered-list',
  },
  {
    getState: (state) => state.unorderedList,
    icon: 'format-list-bulleted',
    label: 'Bulleted list',
    onPress: (input) => input.toggleUnorderedList(),
    testId: 'bulleted-list',
  },
];

const ThemedEnrichedTextInput = withUnistyles(EnrichedTextInput, (theme) => ({
  cursorColor: theme.colors.typography,
  htmlStyle: {
    a: {
      color: theme.colors.primary,
      textDecorationLine: underlineTextDecoration,
    },
    blockquote: {
      borderColor: theme.colors.borderSecondary,
      borderWidth: StyleSheet.hairlineWidth,
      color: theme.colors.typographySecondary,
      gapWidth: theme.space('sm'),
    },
    code: {
      backgroundColor: theme.colors.surfaceMuted,
      color: theme.colors.typography,
    },
    codeblock: {
      backgroundColor: theme.colors.surfaceMuted,
      borderRadius: theme.borderRadius.md,
      color: theme.colors.typography,
    },
    h1: { bold: true, fontSize: theme.fontSize['3xl'] },
    h2: { bold: true, fontSize: theme.fontSize['2xl'] },
    h3: { bold: true, fontSize: theme.fontSize.xl },
    h4: { bold: true, fontSize: theme.fontSize.lg },
    h5: { bold: true, fontSize: theme.fontSize.md },
    h6: { bold: true, fontSize: theme.fontSize.sm },
    ol: {
      markerColor: theme.colors.typography,
      markerFontWeight: theme.fontWeight.normal,
    },
    ul: {
      bulletColor: theme.colors.typography,
      bulletSize: theme.size(2),
    },
    ulCheckbox: {
      boxColor: theme.colors.primary,
      boxSize: theme.size(5),
      gapWidth: theme.space('sm'),
      marginLeft: theme.space('md'),
    },
  },
  placeholderTextColor: theme.colors.typographySecondary,
  selectionColor: theme.colors.typography,
}));

const ActiveToolbarIcon = withUnistyles(MaterialCommunityIcons, (theme) => ({
  color: theme.colors.white,
  size: theme.size(6),
}));

const InactiveToolbarIcon = withUnistyles(MaterialCommunityIcons, (theme) => ({
  color: theme.colors.secondary,
  size: theme.size(6),
}));

function ComposerActionButton({
  action,
  inputRef,
  state,
}: {
  action: ComposerAction;
  inputRef: RefObject<EnrichedTextInputInstance | null>;
  state: OnChangeStateEvent | null;
}) {
  const actionState = state ? action.getState(state) : null;
  const isActive = actionState?.isActive ?? false;

  return (
    <Button
      aria-pressed={isActive}
      accessibilityLabel={action.label}
      accessibilityState={{
        disabled: actionState?.isBlocking ?? false,
        selected: isActive,
      }}
      appearance={isActive ? 'solid' : 'outline'}
      disabled={actionState?.isBlocking}
      hitSlop={2}
      iconOnly
      onPress={() => {
        const input = inputRef.current;
        if (input) {
          action.onPress(input);
          input.focus();
        }
      }}
      size="md"
      style={styles.actionButton}
      testID={`RichTextComposer-${action.testId}`}
      variant={isActive ? 'primary' : 'secondary'}
    >
      <Box center style={styles.toolbarIconSlot}>
        {isActive ? (
          <ActiveToolbarIcon name={action.icon} />
        ) : (
          <InactiveToolbarIcon name={action.icon} />
        )}
      </Box>
    </Button>
  );
}

type ComposerContextValue = {
  inputRef: RefObject<EnrichedTextInputInstance | null>;
  formatState: OnChangeStateEvent | null;
  setFormatState: (state: OnChangeStateEvent) => void;
  inputProps: Omit<RichTextComposerProps, 'children' | 'ref'>;
};

const ComposerContext = createContext<ComposerContextValue | null>(null);

function useComposer() {
  const composer = use(ComposerContext);
  if (!composer) throw new Error('Rich text controls require RichTextComposer');
  return composer;
}

export default function RichTextComposer({
  children,
  ref,
  ...inputProps
}: RichTextComposerProps) {
  const inputRef = useRef<EnrichedTextInputInstance>(null);
  const [formatState, setFormatState] = useState<OnChangeStateEvent | null>(
    null,
  );

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
  }));

  return (
    <ComposerContext
      value={{ inputRef, formatState, setFormatState, inputProps }}
    >
      {children}
    </ComposerContext>
  );
}

export function RichTextComposerEditor() {
  const { inputRef, setFormatState, inputProps } = useComposer();
  const {
    accessibilityLabel = 'Rich text composer',
    autoFocus,
    defaultValue = '',
    errorMessage,
    onChangeHtml,
    onChangeText,
    placeholder = 'Enter your notes here',
  } = inputProps;
  return (
    <Box flex style={styles.composer}>
      <Box flex style={styles.inputShell}>
        <ThemedEnrichedTextInput
          accessibilityLabel={accessibilityLabel}
          autoCapitalize="sentences"
          autoFocus={autoFocus}
          defaultValue={defaultValue}
          onChangeHtml={(event) => onChangeHtml?.(event.nativeEvent.value)}
          onChangeState={(event) => setFormatState(event.nativeEvent)}
          onChangeText={(event) => onChangeText?.(event.nativeEvent.value)}
          placeholder={placeholder}
          ref={inputRef}
          scrollEnabled
          style={styles.input}
          submitBehavior="newline"
          useHtmlNormalizer
        />
      </Box>
      {errorMessage ? (
        <Text
          accessibilityLiveRegion="assertive"
          accessibilityRole="alert"
          px="lg"
          py="sm"
          size="sm"
          variant="error"
        >
          {errorMessage}
        </Text>
      ) : null}
    </Box>
  );
}

export function RichTextComposerToolbar({
  children,
}: {
  children?: ReactNode;
}) {
  const { inputRef, formatState } = useComposer();
  return (
    <Box style={styles.toolbar}>
      <Box
        alignItems="center"
        direction="horizontal"
        justify="center"
        style={styles.toolbarContent}
      >
        {composerActions.map((action) => (
          <ComposerActionButton
            action={action}
            inputRef={inputRef}
            key={action.testId}
            state={formatState}
          />
        ))}
      </Box>
      {children ? <Box style={styles.toolbarAccessory}>{children}</Box> : null}
    </Box>
  );
}

const styles = StyleSheet.create((theme) => ({
  actionButton: {
    borderCurve: 'continuous',
    borderRadius: theme.borderRadius.md,
    height: theme.size(10),
    minHeight: theme.size(10),
    minWidth: theme.size(10),
    width: theme.size(10),
  },
  composer: {
    backgroundColor: theme.colors.surface,
    minWidth: 0,
    overflow: 'hidden',
  },
  input: {
    alignSelf: 'stretch',
    backgroundColor: theme.colors.surface,
    borderWidth: 0,
    boxSizing: 'border-box',
    color: theme.colors.typography,
    flex: 1,
    fontFamily: richTextSystemFontFamily,
    fontSize: theme.fontSize.lg,
    maxWidth: '100%',
    minHeight: 0,
    paddingHorizontal: theme.space('lg'),
  },
  inputShell: {
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
  },
  toolbar: {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.borderSecondary,
    borderTopWidth: StyleSheet.hairlineWidth,
    minWidth: 0,
    overflow: 'hidden',
    paddingTop: theme.space('sm'),
  },
  toolbarAccessory: {
    paddingHorizontal: theme.space('md'),
    paddingVertical: theme.space('sm'),
  },
  toolbarContent: {
    alignItems: 'center',
    gap: theme.space('sm'),
    minWidth: 0,
  },
  toolbarIconSlot: {
    height: theme.size(6),
    width: theme.size(6),
  },
}));
